import type { ReactNode } from 'react';
import type { TextStyle } from 'react-native';
import { Avatar, Box, HStack, Text, VStack } from '../../../components/primitives';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';

/**
 * The one comment anatomy in the product. Internal notes, LinkedIn post
 * comments and reaction authors are all the same shape — a person, what
 * qualifies them, when, and what they said — so they read as one thread
 * language instead of three near-identical layouts that drifted apart.
 *
 * A comment is never a box. It sits directly on the card surface: the avatar
 * gutter is the only structure it needs, and vertical space separates one from
 * the next. Bordering each comment turns a conversation into a stack of slabs.
 */

export const COMMENT_GUTTER = 28;

interface CommentRowProps {
  authorName: string;
  /** Real profile photo when the source gave us one; initials otherwise. */
  authorImageUrl?: string | null;
  /** Headline, role or any short qualifier. Sits inline after the name. */
  meta?: string | null;
  /** Relative time, right-aligned on the name line. */
  timestamp?: string | null;
  body?: string | null;
  /** Replaces the initials avatar — used for AI-authored notes. */
  avatarSlot?: ReactNode;
  /** A small control pinned after the name, e.g. enrich this commenter. */
  action?: ReactNode;
  /** A badge or chip pinned to the far right, e.g. the reaction type. */
  trailing?: ReactNode;
  bodyStyle?: TextStyle;
  testID?: string;
}

export function CommentRow({
  authorName,
  authorImageUrl,
  meta,
  timestamp,
  body,
  avatarSlot,
  action,
  trailing,
  bodyStyle,
  testID,
}: Readonly<CommentRowProps>) {
  return (
    <HStack spacing="sm" align="start" testID={testID}>
      {avatarSlot ?? <Avatar size="sm" name={authorName} src={authorImageUrl ?? undefined} />}

      <VStack spacing="xxs" style={{ flex: 1, minWidth: 0 }}>
        <HStack spacing="xs" align="center">
          <Text
            variant="bodySmall"
            color={colors.text.primary}
            numberOfLines={1}
            style={{ fontWeight: '600', flexShrink: 1 }}
          >
            {authorName}
          </Text>
          {action}
          {Boolean(meta) && (
            <>
              <Text variant="caption" color={colors.text.tertiary}>
                {'·'}
              </Text>
              <Text
                variant="caption"
                color={colors.text.tertiary}
                numberOfLines={1}
                style={{ flexShrink: 1, minWidth: 0 }}
              >
                {meta}
              </Text>
            </>
          )}
          <Box flex={1} />
          {Boolean(timestamp) && (
            <Text variant="caption" color={colors.text.tertiary} style={{ flexShrink: 0 }}>
              {timestamp}
            </Text>
          )}
          {trailing}
        </HStack>

        {Boolean(body) && (
          <Text
            variant="bodySmall"
            color={colors.text.secondary}
            style={{ lineHeight: 18, ...bodyStyle }}
          >
            {body}
          </Text>
        )}
      </VStack>
    </HStack>
  );
}

/**
 * The avatar slot for a non-human author. A tinted well plus a glyph, never a
 * ring — a bordered circle inside a card is a second border.
 */
export function GlyphAvatar({
  glyph,
  well = colors.primary.muted,
}: Readonly<{ glyph: ReactNode; well?: string }>) {
  return (
    <Box
      width={COMMENT_GUTTER}
      height={COMMENT_GUTTER}
      borderRadius={COMMENT_GUTTER / 2}
      backgroundColor={well}
      alignItems="center"
      justifyContent="center"
    >
      {glyph}
    </Box>
  );
}

/** Vertical rhythm between comments in a thread. */
export const COMMENT_THREAD_SPACING = tokens.spacing.lg;
